import React from 'react';
import binanceSquareIcon from '../../images/buttons/binance_square_icon.png';
import xIcon from '../../images/buttons/x_icon.png';
import savePhotoIcon from '../../images/buttons/save_photo_icon.png';
import arrowUpIcon from '../../images/arrow_up.png';
import pinaiLogo from '../../images/pinai_logo.png';

/**
 * Reusable share buttons component for feature cards
 * @param {Object} props
 * @param {Function} props.onShareToBinanceSquare - Optional custom handler for sharing to Binance Square
 * @param {Function} props.onShareToX - Optional custom handler for sharing to X/Twitter
 * @param {Function} props.onSavePhoto - Optional custom handler for saving the card as an image
 * @param {string} props.cardId - ID of the card element to be captured for image saving
 * @param {Object} props.data - Data to be shared
 * @param {string} props.featureName - Name of the feature (used for sharing)
 * @param {string} props.shareText - Optional custom text for sharing
 * @param {boolean} props.compact - Whether to use compact layout (horizontal) or original layout (vertical)
 * @returns {JSX.Element}
 */
const ShareButtons = ({
  onShareToBinanceSquare,
  onShareToX,
  onSavePhoto,
  cardId,
  data,
  featureName,
  shareText,
  compact = false
}) => {
  // Generic handler for Binance Square sharing
  const handleShareToBinanceSquare = () => {
    if (!data) {
      console.error('data not found, cannot share');
      return;
    }
    
    // 如果提供了自定义的分享函数，则使用它
    if (onShareToBinanceSquare) {
      onShareToBinanceSquare(data, featureName, cardId);
      return;
    }
    
    // 默认的分享到Binance Square实现
    try {
      // 构造分享文本
      const defaultShareText = shareText || 
        `I just discovered my ${featureName}: ${data.tag} - ${data.title}\n\n${data.investmentStyle || ''}\n\n#BinanceAI`;
      
      // 检查是否在币安小程序环境中
      if (
        typeof window !== "undefined" &&
        window.bn &&
        typeof window.bn.shareToSquare === "function"
      ) {
        window.bn.shareToSquare({
          text: defaultShareText,
          success: () => {
            console.log('Shared to Binance Square successfully');
          },
          fail: (err) => {
            console.error('Failed to share to Binance Square', err);
            alert('Failed to share to Binance Square');
          }
        });
      } else {
        console.error('Binance Square sharing not available');
        alert('Binance Square sharing is not available in this environment');
      }
    } catch (error) {
      console.error('Share to Binance Square error', error);
    }
  };

  // Generic handler for X/Twitter sharing
  const handleShareToX = async () => {
    if (!data) {
      console.error('data not found, cannot share');
      return;
    }
    
    // 如果提供了自定义的分享函数，则使用它
    if (onShareToX) {
      onShareToX(data, featureName);
      return;
    }
    
    // 默认的分享到X实现 - 直接分享文本
    try {
      // 构造默认分享文本
      const defaultShareText = shareText || 
        `I just discovered my ${featureName}: ${data.tag} - ${data.title}\n\n${data.investmentStyle || ''}\n\n#BinanceAI`;
      
      const encodedText = encodeURIComponent(defaultShareText);
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;
      
      // 打开Twitter分享页面
      window.open(twitterUrl, "_blank");
    } catch (error) {
      console.error('Share to X error', error);
      alert('Failed to share to X. Please try again.');
    }
  };

  // 显示全屏图片预览 - 适用于移动设备
  const showFullImage = (imgUrl) => {
    // 创建全屏覆盖层
    const fullImageOverlay = document.createElement('div');
    fullImageOverlay.style.position = 'fixed';
    fullImageOverlay.style.top = '0';
    fullImageOverlay.style.left = '0';
    fullImageOverlay.style.width = '100%';
    fullImageOverlay.style.height = '100%';
    fullImageOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
    fullImageOverlay.style.zIndex = '10000';
    fullImageOverlay.style.display = 'flex';
    fullImageOverlay.style.flexDirection = 'column';
    fullImageOverlay.style.alignItems = 'center';
    fullImageOverlay.style.justifyContent = 'center';
    fullImageOverlay.style.padding = '20px';
    fullImageOverlay.style.boxSizing = 'border-box';
    fullImageOverlay.style.overflow = 'auto'; // 添加滚动支持
    
    // 创建图片容器
    const imageContainer = document.createElement('div');
    imageContainer.style.position = 'relative';
    imageContainer.style.width = '100%';
    imageContainer.style.maxWidth = '90vw'; // 限制最大宽度
    imageContainer.style.display = 'flex';
    imageContainer.style.flexDirection = 'column'; // 改为列布局
    imageContainer.style.justifyContent = 'center';
    imageContainer.style.alignItems = 'center';
    imageContainer.style.margin = '0 auto'; // 居中
    
    // 创建图片元素
    const fullImage = document.createElement('img');
    fullImage.src = imgUrl;
    fullImage.style.width = '100%';
    fullImage.style.maxWidth = '100%';
    fullImage.style.maxHeight = '80vh'; // 限制高度为视口高度的80%
    fullImage.style.objectFit = 'contain';
    fullImage.style.display = 'block';
    fullImage.style.boxShadow = '0 0 20px rgba(0, 0, 0, 0.5)';
    fullImage.style.borderRadius = '8px';
    
    // 创建关闭按钮
    const closeButton = document.createElement('div');
    closeButton.textContent = '×';
    closeButton.style.position = 'absolute';
    closeButton.style.top = '10px';
    closeButton.style.right = '10px';
    closeButton.style.width = '40px';
    closeButton.style.height = '40px';
    closeButton.style.borderRadius = '50%';
    closeButton.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
    closeButton.style.color = 'white';
    closeButton.style.display = 'flex';
    closeButton.style.justifyContent = 'center';
    closeButton.style.alignItems = 'center';
    closeButton.style.fontSize = '24px';
    closeButton.style.fontWeight = 'bold';
    closeButton.style.cursor = 'pointer';
    closeButton.style.transition = 'all 0.2s ease';
    closeButton.style.zIndex = '10001'; // 确保在最上层
    
    // 添加关闭事件
    closeButton.onclick = (e) => {
      e.stopPropagation(); // 防止事件冒泡
      fullImageOverlay.style.opacity = '0';
      setTimeout(() => document.body.removeChild(fullImageOverlay), 300);
    };
    
    // 点击背景关闭
    fullImageOverlay.addEventListener('click', (e) => {
      if (e.target === fullImageOverlay || e.target === imageContainer) {
        fullImageOverlay.style.opacity = '0';
        setTimeout(() => document.body.removeChild(fullImageOverlay), 300);
      }
    });
    
    // 添加长按保存提示
    const saveHint = document.createElement('div');
    saveHint.style.width = '100%';
    saveHint.style.textAlign = 'center';
    saveHint.style.color = "white";
    saveHint.style.padding = '15px';
    saveHint.style.fontSize = '16px';
    saveHint.style.marginTop = '15px'; // 添加上边距
    saveHint.style.borderRadius = '8px';
    saveHint.style.fontWeight = 'bold';
    saveHint.style.zIndex = '10001'; // 确保在最上层
    
    // 创建箭头图片元素
    const arrowUp = document.createElement('img');
    arrowUp.src = arrowUpIcon;
    arrowUp.style.margin = '0 auto 10px auto';
    arrowUp.style.display = 'block';
    arrowUp.style.width = '24px';
    arrowUp.style.height = '48px';
    // 箭头应该向上指，不需要旋转
    
    // 添加文本元素
    const textNode = document.createElement('div');
    textNode.textContent = 'Long press the image to save';
    
    // 将箭头和文本添加到提示容器中
    saveHint.appendChild(arrowUp);
    saveHint.appendChild(textNode);
    
    // 组装并添加到页面
    imageContainer.appendChild(fullImage);
    imageContainer.appendChild(saveHint); // 将提示放入容器内
    fullImageOverlay.appendChild(imageContainer);
    fullImageOverlay.appendChild(closeButton);
    
    // 添加到页面并设置动画
    document.body.appendChild(fullImageOverlay);
    fullImageOverlay.style.opacity = '0';
    setTimeout(() => {
      fullImageOverlay.style.opacity = '1';
    }, 10);
  };

  // 通用的保存图片处理函数
  const handleSavePhoto = () => {
    if (!cardId) {
      console.error('cardId not found, cannot save photo');
      return;
    }
    
    // 如果提供了自定义的保存函数，则使用它
    if (onSavePhoto) {
      onSavePhoto(cardId);
      return;
    }
    
    // 默认的保存图片实现
    try {
      // 获取卡片元素
      const cardElement = document.getElementById(cardId);

      if (!cardElement) {
        console.error(`Cannot find card element #${cardId}`);
        return;
      }
      
      // 处理滚动内容 - 临时移除滚动样式以捕获完整内容
      const scrollElements = cardElement.querySelectorAll('[style*="overflow"]');
      const originalStyles = [];
      
      scrollElements.forEach(el => {
        // 保存原始样式
        originalStyles.push({
          element: el,
          style: el.getAttribute('style')
        });
        
        // 移除滚动和高度限制，确保内容完全展开
        const style = el.getAttribute('style') || '';
        const newStyle = style
          .replace(/overflow[^;]*;/g, '')
          .replace(/height[^;]*;/g, '')
          .replace(/max-height[^;]*;/g, '');
        
        el.setAttribute('style', newStyle);
      });

      // 检测是否为移动端
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

      // 使用html2canvas渲染新卡片
      import("html2canvas")
        .then((html2canvasModule) => {
          const html2canvas = html2canvasModule.default;

          html2canvas(cardElement, {
            allowTaint: true,
            useCORS: true,
            scale: 2, // 提高图片质量
            backgroundColor: "#0a0e1f", // 背景颜色与卡片背景相同
            height: cardElement.scrollHeight, // 确保捕获整个高度
            windowHeight: cardElement.scrollHeight // 确保捕获整个高度
          })
            .then((canvas) => {
              // 恢复原始样式
              originalStyles.forEach(item => {
                if (item.style) {
                  item.element.setAttribute('style', item.style);
                } else {
                  item.element.removeAttribute('style');
                }
              });
              
              // 添加PINAI logo和文字到canvas底部
              const logoImg = new Image();
              logoImg.crossOrigin = "Anonymous";
              logoImg.src = pinaiLogo;
              
              logoImg.onload = () => {
                // 设置底部区域高度
                const footerHeight = 80;
                const newHeight = canvas.height + footerHeight;
                
                // 创建新的canvas以包含footer
                const newCanvas = document.createElement('canvas');
                newCanvas.width = canvas.width;
                newCanvas.height = newHeight;
                const newCtx = newCanvas.getContext('2d');
                
                // 绘制原始canvas内容
                newCtx.drawImage(canvas, 0, 0);
                
                // 绘制底部背景
                newCtx.fillStyle = '#0a0e1f';
                newCtx.fillRect(0, canvas.height, canvas.width, footerHeight);
                
                // 绘制PINAI logo
                const logoSize = 60;
                const logoX = (canvas.width - logoSize) / 2 - 60;
                const logoY = canvas.height + (footerHeight - logoSize) / 2;
                newCtx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
                
                // 绘制文字
                newCtx.font = 'bold 18px Arial';
                newCtx.fillStyle = '#ffffff';
                newCtx.textAlign = 'left';
                newCtx.textBaseline = 'middle';
                newCtx.fillText('Powered by PINAI', logoX + logoSize + 12, canvas.height + footerHeight / 2);
                
                // 转换canvas为图片URL
                const imgUrl = newCanvas.toDataURL("image/png");

                // 在币安小程序环境中保存图片
                if (
                  typeof window !== "undefined" &&
                  window.bn &&
                  typeof window.bn.saveImageToPhotosAlbum === "function"
                ) {
                  window.bn.saveImageToPhotosAlbum({
                    filePath: imgUrl,
                    success: () => {
                      console.log("Image saved to album");
                      alert("图片已保存到相册");
                    },
                    fail: (err) => {
                      console.error("Failed to save image", err);
                      // 直接显示全屏图片
                      showFullImage(imgUrl);
                    },
                  });
                } else if (isMobile) {
                  // 移动端直接显示全屏图片，无需先显示缩略图
                  showFullImage(imgUrl);
                } else {
                  // PC端直接下载图片
                  const link = document.createElement('a');
                  link.download = `${featureName || 'image'}-${Date.now()}.png`;
                  link.href = imgUrl;
                  link.click();
                }
              };
              
              logoImg.onerror = () => {
                console.error("Failed to load PINAI logo");
                // 如果logo加载失败，仍然使用原始canvas
                const imgUrl = canvas.toDataURL("image/png");
                
                // 处理图片URL (与上面相同的逻辑)
                if (
                  typeof window !== "undefined" &&
                  window.bn &&
                  typeof window.bn.saveImageToPhotosAlbum === "function"
                ) {
                  window.bn.saveImageToPhotosAlbum({
                    filePath: imgUrl,
                    success: () => {
                      console.log("Image saved to album");
                      alert("图片已保存到相册");
                    },
                    fail: (err) => {
                      console.error("Failed to save image", err);
                      showFullImage(imgUrl);
                    },
                  });
                } else if (isMobile) {
                  showFullImage(imgUrl);
                } else {
                  const link = document.createElement('a');
                  link.download = `${featureName || 'image'}-${Date.now()}.png`;
                  link.href = imgUrl;
                  link.click();
                }
              };
            })
            .catch((err) => {
              // 恢复原始样式
              originalStyles.forEach(item => {
                if (item.style) {
                  item.element.setAttribute('style', item.style);
                } else {
                  item.element.removeAttribute('style');
                }
              });
              
              console.error("html2canvas error:", err);
              alert("Failed to generate image, please try again later");
            });
        })
        .catch((err) => {
          // 恢复原始样式
          originalStyles.forEach(item => {
            if (item.style) {
              item.element.setAttribute('style', item.style);
            } else {
              item.element.removeAttribute('style');
            }
          });
          
          console.error("Failed to load html2canvas:", err);
          alert("Failed to load image generation tool");
        });
    } catch (error) {
      console.error("Error saving image", error);
      alert("Error saving image");
    }
  };

  // Conditionally render based on compact prop
  return compact ? (
    // Compact horizontal layout
    <div className="flex justify-center items-center gap-2 w-full">
      {/* 分享到Binance Square按钮 */}
      <button
        onClick={handleShareToBinanceSquare}
        className="flex items-center justify-center gap-1 py-2 px-3 bg-[#F0B90B] rounded-[8px] shadow-[0px_1px_2px_0px_rgba(12,12,13,0.05)] hover:opacity-90 transition-all flex-1"
      >
        <div className="w-4 h-4 overflow-hidden flex-shrink-0">
          <img src={binanceSquareIcon} alt="Binance" className="w-full h-full object-cover" />
        </div>
        <span className="text-[#1B1C1F] font-medium text-xs whitespace-nowrap">Binance</span>
      </button>
      
      {/* 分享到X按钮 */}
      <button
        onClick={handleShareToX}
        className="flex items-center justify-center gap-1 py-2 px-3 bg-black rounded-[8px] shadow-[0px_1px_2px_0px_rgba(12,12,13,0.05)] hover:opacity-90 transition-all flex-1"
      >
        <div className="w-4 h-4 overflow-hidden flex-shrink-0">
          <img src={xIcon} alt="X" className="w-full h-full object-cover" />
        </div>
        <span className="text-white font-medium text-xs whitespace-nowrap">Share to X</span>
      </button>
      
      {/* 保存图片按钮 */}
      <button
        onClick={handleSavePhoto}
        className="flex items-center justify-center gap-1 py-2 px-3 bg-white rounded-[8px] border border-gray-200 shadow-[0px_1px_4px_0px_rgba(12,12,13,0.05),0px_1px_4px_0px_rgba(12,12,13,0.1)] hover:opacity-90 transition-all flex-1"
      >
        <div className="w-4 h-4 overflow-hidden flex-shrink-0">
          <img src={savePhotoIcon} alt="Save" className="w-full h-full object-cover" />
        </div>
        <span className="text-[#1B1C1F] font-medium text-xs whitespace-nowrap">Save</span>
      </button>
    </div>
  ) : (
    // Original vertical layout
    <div className="flex flex-col items-center gap-4 w-full mt-6">
      {/* 分享到Binance Square按钮 */}
      <button
        onClick={handleShareToBinanceSquare}
        className="flex items-center justify-center gap-2 py-3 px-4 bg-[#F0B90B] rounded-[12px] w-[80%] max-w-[300px] shadow-[0px_1px_4px_0px_rgba(12,12,13,0.05),0px_1px_4px_0px_rgba(12,12,13,0.1)] hover:opacity-90 transition-all"
      >
        <div className="w-5 h-5 overflow-hidden flex-shrink-0">
          <img src={binanceSquareIcon} alt="Binance" className="w-full h-full object-cover" />
        </div>
        <span className="text-[#1B1C1F] font-medium text-sm whitespace-nowrap">Share to Binance Square</span>
      </button>
      
      {/* 分享到X按钮 */}
      <button
        onClick={handleShareToX}
        className="flex items-center justify-center gap-2 py-3 px-4 bg-black rounded-[12px] w-[80%] max-w-[300px] shadow-[0px_1px_4px_0px_rgba(12,12,13,0.05),0px_1px_4px_0px_rgba(12,12,13,0.1)] hover:opacity-90 transition-all"
      >
        <div className="w-5 h-5 overflow-hidden flex-shrink-0">
          <img src={xIcon} alt="X" className="w-full h-full object-cover" />
        </div>
        <span className="text-white font-medium text-sm whitespace-nowrap">Share to X</span>
      </button>
      
      {/* 保存图片按钮 */}
      <button
        onClick={handleSavePhoto}
        className="flex items-center justify-center gap-2 py-3 px-4 bg-[rgba(255,255,255,0.75)] rounded-[12px] w-[80%] max-w-[300px] shadow-[0px_1px_4px_0px_rgba(12,12,13,0.05),0px_1px_4px_0px_rgba(12,12,13,0.1)] hover:opacity-90 transition-all"
      >
        <div className="w-5 h-5 overflow-hidden flex-shrink-0">
          <img src={savePhotoIcon} alt="Save" className="w-full h-full object-cover" />
        </div>
        <span className="text-[#1B1C1F] font-medium text-sm whitespace-nowrap">Save Photo</span>
      </button>
    </div>
  );
};

export default ShareButtons;
